import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileCode, ArrowRight } from 'lucide-react';
import {
  buildExtractPreview,
  type ExtractPreview,
} from '@/utils/scadExtractUtils';

export interface ExtractToFilePayload {
  filename: string;
  fileContent: string;
  callCode: string;
  importStatement: string;
  /** The original selection range in Monaco (1-indexed) */
  selectionRange: {
    startLineNumber: number;
    startColumn: number;
    endLineNumber: number;
    endColumn: number;
  };
}

interface ExtractToFileDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: ExtractToFilePayload) => void;
  /** The selected text from the editor */
  selectedCode: string;
  /** The full editor content */
  fullCode: string;
  /** 1-indexed selection range */
  selectionRange: {
    startLineNumber: number;
    startColumn: number;
    endLineNumber: number;
    endColumn: number;
  } | null;
}

export function ExtractToFileDialog({
  open,
  onClose,
  onConfirm,
  selectedCode,
  fullCode,
  selectionRange,
}: ExtractToFileDialogProps) {
  const [filename, setFilename] = useState('');
  const [preview, setPreview] = useState<ExtractPreview | null>(null);
  const [filenameError, setFilenameError] = useState('');

  // Recompute preview whenever the filename changes
  useEffect(() => {
    if (!open || !selectedCode || !selectionRange) {
      setPreview(null);
      return;
    }

    const trimmed = filename.trim();
    if (!trimmed) {
      setPreview(null);
      setFilenameError('');
      return;
    }

    // Validate: must be a valid identifier-based filename
    const base = trimmed.replace(/\.scad$/i, '');
    if (!/^[a-zA-Z_$][a-zA-Z0-9_$. -]*$/.test(base)) {
      setFilenameError('Filename must start with a letter or underscore');
      setPreview(null);
      return;
    }

    setFilenameError('');

    const p = buildExtractPreview(
      selectedCode,
      fullCode,
      selectionRange.startLineNumber,
      selectionRange.endLineNumber,
      trimmed,
    );
    setPreview(p);
  }, [filename, open, selectedCode, fullCode, selectionRange]);

  const resetForm = () => {
    setFilename('');
    setPreview(null);
    setFilenameError('');
  };

  const finalFilename = filename.trim().endsWith('.scad')
    ? filename.trim()
    : `${filename.trim()}.scad`;

  const handleConfirm = () => {
    if (!preview || !selectionRange || !filename.trim()) return;
    onConfirm({
      filename: finalFilename,
      fileContent: preview.fileContent,
      callCode: preview.callCode,
      importStatement: preview.importStatement,
      selectionRange,
    });
    onClose();
  };

  const isModule = preview?.selectionType === 'module';

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (v) {
          resetForm();
        } else {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCode className="h-5 w-5" />
            Extract to File
          </DialogTitle>
          <DialogDescription>
            {isModule
              ? 'Move this module definition into a separate file. An include statement and a call to the module will replace it.'
              : 'Wrap the selected code in a module and move it to a separate file. Outer variables used in the code will become module parameters.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Filename input */}
          <div className="space-y-1.5">
            <Label htmlFor="extract-filename">New filename</Label>
            <div className="flex items-center gap-2">
              <Input
                id="extract-filename"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="e.g. gear.scad"
                className="font-mono"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && preview) handleConfirm();
                }}
                autoFocus
              />
              {filename.trim() && !filename.trim().endsWith('.scad') && (
                <span className="shrink-0 text-xs text-muted-foreground">
                  .scad
                </span>
              )}
            </div>
            {filenameError && (
              <p className="text-xs text-destructive">{filenameError}</p>
            )}
          </div>

          {/* Preview */}
          {preview && (
            <div className="space-y-3">
              {/* New file content */}
              <div className="space-y-1">
                <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <FileCode className="h-3.5 w-3.5" />
                  {finalFilename}
                </p>
                <pre className="max-h-48 overflow-auto rounded-md border border-border bg-muted/50 p-3 font-mono text-xs leading-relaxed text-foreground">
                  {preview.fileContent}
                </pre>
              </div>

              {/* Replacement in main file */}
              <div className="space-y-1">
                <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <ArrowRight className="h-3.5 w-3.5" />
                  Replaces selection in main file with:
                </p>
                <pre className="rounded-md border border-border bg-muted/50 p-3 font-mono text-xs leading-relaxed text-foreground">
                  <span className="text-blue-400">
                    {preview.importStatement}
                  </span>
                  {'\n'}
                  <span className="text-foreground/70">
                    {'// ... other code ...'}
                  </span>
                  {'\n'}
                  {preview.callCode}
                </pre>
              </div>
            </div>
          )}

          {!preview && filename.trim() && !filenameError && (
            <p className="text-xs text-muted-foreground">
              Computing preview...
            </p>
          )}

          {!filename.trim() && (
            <p className="text-xs text-muted-foreground">
              Enter a filename to see a preview of what will be extracted.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!preview || !filename.trim()}
          >
            Extract
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
